import {Component, OnInit, OnDestroy} from '@angular/core';
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {RouterLink} from '@angular/router';
import {GameAccountService} from '../../../core/services/user/game-account.service';
import {GameUserInfo} from '../../../core/models/user/game-user-info.model';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors} from '@angular/forms';
import {Subscription} from 'rxjs';
import {ConquerVipService} from '../../../core/services/vip/conquer-vip.service';
import {UserBindVip} from '../../../core/models/vip/user-bind-vip.model';
import {LoadingComponent} from '../../../shared/components/loading/loading.component';
import {RecaptchaV3Service} from '../../../core/services/recaptcha/recaptcha-v3-service';

@Component({
  selector: 'app-conquer-accounts',
  imports: [
    TranslateModule,
    CommonModule,
    RouterLink,
    ReactiveFormsModule,
    LoadingComponent
  ],
  templateUrl: './conquer-accounts.component.html',
  styleUrl: './conquer-accounts.component.scss'
})
export class ConquerAccountsComponent implements OnInit, OnDestroy {

  gameAccounts: GameUserInfo[] = [];

  // Modal visibility flags
  showPasswordModal = false;
  showDeleteModal = false;

  // Selected account for operations
  selectedAccount: GameUserInfo | null = null;

  // Form groups
  passwordForm: FormGroup;
  deleteForm: FormGroup;
  registerForm: FormGroup;

  // Store subscriptions for cleanup
  private subscriptions: Subscription[] = [];

  // Store the confirmation text pattern
  private confirmationPattern = 'I understand';

  // Loading states
  loading: boolean = false;
  registerLoading: boolean = false;
  passwordChangeLoading: boolean = false;
  deleteAccountLoading: boolean = false;

  // Error messages
  registerError: string | null = null;
  passwordChangeError: string | null = null;
  deleteAccountError: string | null = null;

  // VIP info
  vipInfo: UserBindVip | null = null;
  vipLoading: boolean = false;

  constructor(
    private readonly recaptchaService: RecaptchaV3Service,
    private readonly gameAccount: GameAccountService,
    private readonly vipService: ConquerVipService,
    private fb: FormBuilder,
    private translate: TranslateService
  ) {
    // Initialize forms
    this.passwordForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });

    // Initialize deleteForm with a temporary pattern, will be updated in ngOnInit
    this.deleteForm = this.fb.group({
      confirmationText: ['', [Validators.required, Validators.pattern(this.confirmationPattern)]]
    });

    // Initialize registerForm
    this.registerForm = this.fb.group({
      userName: ['', [Validators.required, Validators.minLength(3)]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    // Get game accounts
    this.loading = true;
    this.gameAccount.getGameAccounts()
      .subscribe({
        next: (response) => {
          this.gameAccounts = response;
        },
        error: (err) => {
          console.error('Error loading game accounts', err);
        },
        complete: () => {
          this.loading = false;
        }
      });

    this.vipLoading = true;
    this.vipService.getVipInfo(false)
      .subscribe({
        next: (response) => {
          this.vipInfo = response;
        },
        error: (err) => {
          console.error('Error loading VIP info', err);
          this.vipLoading = false;
        },
        complete: () => {
          this.vipLoading = false;
        }
      });

    // Get the translated confirmation text and update the validation pattern
    this.updateConfirmationPattern();

    // Subscribe to language changes to update the validation pattern
    const langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.updateConfirmationPattern();
    });

    this.subscriptions.push(langChangeSub);
  }

  getAccountStatusText(account: GameUserInfo): string {
    if (account.deleteTime) {
      return this.translate.instant('CONQUER_ACCOUNTS.LIST.ACCOUNT_STATUS.DELETED', {
        date: new Date(account.deleteTime).toLocaleString(),
      });
    } else if (account.isBanned && account.banExpireTime) {
      return this.translate.instant('CONQUER_ACCOUNTS.LIST.ACCOUNT_STATUS.BANNED', {
        date: new Date(account.banExpireTime).toLocaleString(),
      });
    } else if (account.isBanned) {
      return this.translate.instant('CONQUER_ACCOUNTS.LIST.ACCOUNT_STATUS.BANNED_FOREVER');
    } else if (account.isLocked) {
      return this.translate.instant('CONQUER_ACCOUNTS.LIST.ACCOUNT_STATUS.LOCKED');
    }

    return this.translate.instant('CONQUER_ACCOUNTS.LIST.ACCOUNT_STATUS.NORMAL');
  }


  /**
   * Updates the confirmation pattern based on the current language
   */
  private updateConfirmationPattern(): void {
    const sub = this.translate.get('CONQUER_ACCOUNTS.MODALS.DELETE_ACCOUNT.CONFIRMATION_PLACEHOLDER').subscribe(
      (translatedText: string) => {
        this.confirmationPattern = translatedText;

        // Update the validation pattern in the form
        this.deleteForm.get('confirmationText')?.setValidators([
          Validators.required,
          Validators.pattern(this.confirmationPattern)
        ]);

        // Update the form validation
        this.deleteForm.get('confirmationText')?.updateValueAndValidity();
      }
    );

    this.subscriptions.push(sub);
  }

  ngOnDestroy(): void {
    // Clean up subscriptions to prevent memory leaks
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // Password match validator
  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;

    return password === confirmPassword ? null : { 'passwordMismatch': true };
  }

  // Modal methods
  openPasswordChangeModal(account: GameUserInfo): void {
    this.selectedAccount = account;
    this.passwordForm.reset();
    this.showPasswordModal = true;
  }

  closePasswordModal(): void {
    this.showPasswordModal = false;
    this.selectedAccount = null;
    this.passwordChangeError = null;
  }

  openDeleteAccountModal(account: GameUserInfo): void {
    this.selectedAccount = account;
    this.deleteForm.reset();
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.selectedAccount = null;
    this.deleteAccountError = null;
  }

  // Form submission methods
  changePassword(): void {
    if (this.passwordForm.valid && this.selectedAccount) {
      const password = this.passwordForm.get('password')?.value;
      const confirmPassword = this.passwordForm.get('confirmPassword')?.value;

      // Reset error message
      this.passwordChangeError = null;

      // Set loading state
      this.passwordChangeLoading = true;

      this.recaptchaService.execute('changeConquerAccountPassword', (token) => {
        if (!this.selectedAccount) {
          this.passwordChangeLoading = false;
          return;
        }

        this.gameAccount.changePassword(token,
          this.selectedAccount.id,
          password,
          confirmPassword
        ).subscribe({
          next: () => {
            console.log(`Password changed successfully for account ${this.selectedAccount?.userName}`);
            this.closePasswordModal();
          },
          error: (err) => {
            console.error('Error changing password', err);
            if (err.error && err.error.message) {
              this.passwordChangeError = err.error.message;
            } else {
              this.passwordChangeError = 'An unexpected error occurred';
            }
            this.passwordChangeLoading = false;
          },
          complete: () => {
            this.passwordChangeLoading = false;
          }
        });
      });
    }
  }

  deleteAccount(): void {
    if (this.deleteForm.valid && this.selectedAccount) {
      const stringToConfirm = this.deleteForm.get('confirmationText')?.value;

      // Reset error message
      this.deleteAccountError = null;

      // Set loading state
      this.deleteAccountLoading = true;

      this.recaptchaService.execute('deleteConquerAccount', (token) => {
        if (!this.selectedAccount) {
          this.deleteAccountLoading = false;
          return;
        }

        this.gameAccount.deleteAccount(token, this.selectedAccount.id).subscribe({
          next: () => {
            console.log(`Account ${this.selectedAccount?.userName} deleted successfully`);
            // Refresh the accounts list
            this.ngOnInit();
            this.closeDeleteModal();
          },
          error: (err) => {
            console.error('Error deleting account', err);
            if (err.error && err.error.message) {
              this.deleteAccountError = err.error.message;
            } else {
              this.deleteAccountError = 'An unexpected error occurred';
            }
            this.deleteAccountLoading = false;
          },
          complete: () => {
            this.deleteAccountLoading = false;
          }
        });
      });
    }
  }

  registerAccount(): void {
    if (this.registerForm.valid) {
      const userName = this.registerForm.get('userName')?.value;
      const password = this.registerForm.get('password')?.value;
      const confirmPassword = this.registerForm.get('confirmPassword')?.value;

      // Reset error message
      this.registerError = null;

      // Set loading state
      this.registerLoading = true;

      this.recaptchaService.execute('registerConquerAccount', (token) => {
        this.gameAccount.registerAccount(token, userName, password, confirmPassword).subscribe({
          next: () => {
            console.log(`Account ${userName} registered successfully`);
            // Reset the form
            this.registerForm.reset();
            // Refresh the accounts list
            this.ngOnInit();
          },
          error: (err) => {
            console.error('Error registering account', err);
            if (err.error && err.error.message) {
              this.registerError = err.error.message;
            } else {
              this.registerError = 'An unexpected error occurred';
            }
            this.registerLoading = false;
          },
          complete: () => {
            this.registerLoading = false;
          }
        });
      });
    }
  }
}
